import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Chip, Grid, WordCountArea } from 'web-library';
import React from 'react';
import { ConfigurationType } from 'web-library/models/DropDown';
import { MutationStatus } from 'react-query';
import { AccountManagementActionTypesResponseObject } from 'web-library/models/AccountManagementConfiguration';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { UseFormReturn } from 'react-hook-form';
import { useStyles } from './index';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { useGetUserGroup, UserGroupResponse } from '../../../api-refactored/user/useGetUserGroup';

const mapApproversToDropdown = (data?: ConfigurationType[]) => {
  if (data === undefined) {
    return [];
  }

  return data?.map((a) => ({
    code: a.code,
    description: a.description,
  }));
};

interface ActionApproverRemarksContentProps {
  filteredActionTypes: AccountManagementActionTypesResponseObject[];
  apiButtonStatus: MutationStatus;
  postSearchApproversData?: ConfigurationType[];
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  isEditState: boolean;
  isApproverNeededAction: boolean;
  isEdit: boolean;
  formHook?: UseFormReturn<Record<string, any>>;
  isAdmin: boolean;
}

const ActionApproverRemarksContent = ({
  filteredActionTypes,
  apiButtonStatus,
  postSearchApproversData,
  files,
  setFiles,
  isEditState,
  isApproverNeededAction,
  isEdit,
  formHook,
  isAdmin,
}: ActionApproverRemarksContentProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const { data: requestFilesConfigData } = useGetRequestFileConfigs({});

  const {
    data: userGroupData,
    isLoading: isLoadingUserGroup,
    error: errorUserGroup,
    refetch: refetchUserGroup,
  } = useGetUserGroup();

  const getDisplayName = (option: UserGroupResponse) => option.displayName;

  return (
    <Box className={classes.responsiveFlexContainer}>
      <Box className={[classes.formElementContainer, classes.formElementMarginBottom].join(' ')}>
        <Grid container spacing={0}>
          {!isEdit && isAdmin && (
            <Grid md={5} sm={12} xs={12}>
              {formHook && (
                <ControlledAutocomplete<UserGroupResponse, true, false, undefined>
                  label={t('admin->>CreateOpsAdminForm.User Group')}
                  name="userGroup"
                  control={formHook.control}
                  className={classes.formElementContainer}
                  error={!!formHook.formState.errors.userGroup}
                  helperText={formHook.formState.errors?.userGroup?.message?.toString()}
                  loading={isLoadingUserGroup}
                  loadingError={!!errorUserGroup}
                  loadingErrorOnReFetch={refetchUserGroup}
                  options={userGroupData || []}
                  getOptionLabel={getDisplayName}
                  isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
                  placeholder="Select User Group"
                  multiple
                  disableCloseOnSelect
                  defaultValue={[]}
                  renderOption={(props, option: UserGroupResponse, { selected }) => (
                    <>
                      <Checkbox checked={selected} />
                      {option.displayName}
                    </>
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, tagIndex) => (
                      <Chip
                        id="auto-complete"
                        label={option.displayName}
                        {...getTagProps({ index: tagIndex })}
                      />
                    ))
                  }
                />
              )}
            </Grid>
          )}
          {!isEditState && !!filteredActionTypes?.length && (
            <Grid md={5} sm={12} xs={12}>
              {formHook && (
                <ControlledAutocomplete
                  name="action"
                  control={formHook.control}
                  label={t('admin->>UserAccountManagement.Action')}
                  className={classes.actionApprover}
                  options={mapApproversToDropdown(filteredActionTypes)}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                  renderOption={(_,option)=>option.description}
                  placeholder={t('admin->>UserAccountManagement.SelectAction')}
                  error={!!formHook.formState.errors?.action}
                  disableClearable
                  readOnly
                  helperText={formHook.formState.errors?.action?.message?.toString()}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  isRequired
                />
              )}
            </Grid>
          )}
        </Grid>
        {(isApproverNeededAction || isEditState) && (
          <>
            {postSearchApproversData && (
              <Grid container spacing={0}>
                <Grid md={5} sm={12} xs={12}>
                  {formHook && (
                    <ControlledAutocomplete
                      name="approver"
                      control={formHook.control}
                      label={t('admin->>UserAccountManagement.Approver')}
                      className={classes.actionApprover}
                      options={mapApproversToDropdown(postSearchApproversData)}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                      renderOption={(_,option)=>option.description}
                      placeholder={t('admin->>UserAccountManagement.SelectApprover')}
                      error={!!formHook.formState.errors?.approver}
                      disableClearable
                      readOnly
                      helperText={formHook.formState.errors?.approver?.message?.toString()}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      isRequired
                    />
                  )}
                </Grid>
              </Grid>
            )}
            <Grid container spacing={0}>
              <Grid md={5} sm={12} xs={12}>
                {formHook && (
                    <WordCountArea
                        error={!!formHook.formState.errors?.remarks}
                        label={t('admin->>UserAccountManagement.Remarks')}
                        placeholder={t('admin->>UserAccountManagement.EnterRemarks')}
                        maxLength={250}
                        minRows={3}
                        isRequired
                        form={formHook}
                        name="remarks"
                        // CPEPOKPNEH-3391
                        onChange={(e) => {
                            const trimmedValue = e.target.value.slice(0, 250); // enforce maxLength
                            formHook.setValue('remarks', trimmedValue);
                        }}
                    />
                )}
              </Grid>
            </Grid>
            {requestFilesConfigData && (
              <Grid container spacing={2} className={classes.fileDropZoneContainer}>
                <Grid md={5} sm={12} xs={12}>
                  <RequestFileDropZone
                    fileConfig={requestFilesConfigData}
                    files={files}
                    setFiles={setFiles}
                    isLoading={apiButtonStatus === 'loading'}
                  />
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ActionApproverRemarksContent;
